(() => {
  if (window.ChewTrueIntersectionObserver) return;

  const pools = new Map();
  const rootIds = new WeakMap();
  let nextRootId = 1;

  const normalizeThreshold = (threshold = 0) => {
    const values = Array.isArray(threshold) ? threshold : [threshold];
    return values.map(Number).sort((a, b) => a - b);
  };

  const getRootId = (root) => {
    if (!root) return "viewport";
    if (!rootIds.has(root)) rootIds.set(root, nextRootId++);
    return `root-${rootIds.get(root)}`;
  };

  const getPoolKey = ({ root = null, rootMargin = "0px", threshold = 0 }) => {
    return `${getRootId(root)}|${rootMargin}|${normalizeThreshold(threshold).join(",")}`;
  };

  const deletePoolIfEmpty = (pool) => {
    if (pool.targets.size) return;
    pool.observer.disconnect();
    pools.delete(pool.key);
  };

  const removeSubscription = (pool, element, subscription) => {
    const subscriptions = pool.targets.get(element);
    if (!subscriptions) return;

    subscriptions.delete(subscription);
    if (subscriptions.size) return;

    pool.targets.delete(element);
    pool.observer.unobserve(element);
    deletePoolIfEmpty(pool);
  };

  const createPool = (key, options) => {
    const pool = {
      key,
      targets: new Map(),
      observer: null,
    };

    pool.observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        const subscriptions = pool.targets.get(entry.target);
        if (!subscriptions) return;

        [...subscriptions].forEach((subscription) => {
          if (subscription.once && !entry.isIntersecting) return;
          if (subscription.once) {
            removeSubscription(pool, entry.target, subscription);
          }

          try {
            subscription.callback(entry);
          } catch (error) {
            console.error("Intersection observer callback failed:", error);
          }
        });
      });
    }, options);

    pools.set(key, pool);
    return pool;
  };

  const observe = (element, callback, options = {}) => {
    if (!(element instanceof Element) || typeof callback !== "function") {
      return () => {};
    }

    if (!("IntersectionObserver" in window)) {
      callback({ target: element, isIntersecting: true, intersectionRatio: 1 });
      return () => {};
    }

    const observerOptions = {
      root: options.root || null,
      rootMargin: options.rootMargin || "0px",
      threshold: normalizeThreshold(options.threshold ?? 0),
    };
    const key = getPoolKey(observerOptions);
    const pool = pools.get(key) || createPool(key, observerOptions);
    const subscription = {
      callback,
      once: options.once === true,
    };
    const subscriptions = pool.targets.get(element) || new Set();

    subscriptions.add(subscription);
    pool.targets.set(element, subscriptions);
    pool.observer.observe(element);

    return () => removeSubscription(pool, element, subscription);
  };

  const observeOnce = (element, callback, options = {}) => {
    return observe(element, callback, { ...options, once: true });
  };

  const unobserve = (element) => {
    pools.forEach((pool) => {
      if (!pool.targets.has(element)) return;
      pool.targets.delete(element);
      pool.observer.unobserve(element);
      deletePoolIfEmpty(pool);
    });
  };

  const unobserveTree = (root) => {
    if (!(root instanceof Element)) return;

    pools.forEach((pool) => {
      [...pool.targets.keys()].forEach((element) => {
        if (element === root || root.contains(element)) {
          pool.targets.delete(element);
          pool.observer.unobserve(element);
        }
      });
      deletePoolIfEmpty(pool);
    });
  };

  const disconnect = () => {
    pools.forEach((pool) => pool.observer.disconnect());
    pools.clear();
  };

  window.ChewTrueIntersectionObserver = Object.freeze({
    observe,
    observeOnce,
    unobserve,
    unobserveTree,
    disconnect,
  });

  document.addEventListener("shopify:section:unload", (event) => {
    unobserveTree(event.target);
  });
})();
